<template>
	<div class="selection-list">
		<div class="selection-list__available">
			<div class="selection-list__list-header">
				<p class="selection-list__list-title">
					Available {{ title }}
					<span class="selection-list__list-title-counter">
						({{ totalItemsAvailables }})
					</span>
				</p>
				<Button
					v-if="displaySelectAllButton"
					:id="`${uid}_select_button`"
					className="btn--link"
					labelText="Select All"
					:btnClick="SelectAll"
					:disabled="disableSelectButton"
				/>
			</div>

			<div class="selection-list-container">
				<div class="selection-list__search">
					<Search
						variant="toggle-icons"
						:placeholder="placeholder"
						v-model.lazy="search"
					/>
				</div>
				<div
					class="selection-list__list-help-txt"
					v-if="availables.length < 1"
				>
					The available list is empty. {{ availableText }}
				</div>
				<ul class="selection-list__items">
					<li
						class="selection-list__item"
						v-for="item in availables"
						:key="item[mapping.id]"
					>
						<Checkbox
							:labelText="item[mapping.name]"
							:name="item[mapping.name]"
							:value="item[mapping.id]"
							@input="changeValueToSelected(item)"
							:modelValue="null"
						/>
					</li>
				</ul>
			</div>
		</div>
		<div class="selection-list__selected">
			<div class="selection-list__list-header">
				<p class="selection-list__list-title">
					Selected {{ title }}
					<span class="selection-list__list-title-counter">
						({{ totalItemsSelected }})
					</span>
				</p>
				<Button
					v-if="displayUnSelectAllButton"
					:id="`${uid}_unselect_button`"
					className="btn--link"
					labelText="Unselect All"
					:btnClick="unSelectAll"
					:disabled="disableUnselectButton"
				/>
			</div>
			<div class="selection-list-container">
				<div
					class="selection-list__list-help-txt"
					v-if="selected.length < 1"
				>
					{{ emptyText }}
				</div>
				<ul
					class="selection-list__items"
					:class="[selectedItems ? 'has-items' : '']"
				>
					<li
						class="selection-list__item"
						v-for="item in selected"
						:key="item[mapping.id]"
					>
						<Checkbox
							:labelText="item[mapping.name]"
							:name="item[mapping.name]"
							:value="item[mapping.id]"
							@input="changeValueToAvailable(item)"
							:modelValue="item[mapping.id]"
						/>
					</li>
				</ul>
			</div>
		</div>
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import Checkbox from '@/components/forms/selection-control/checkbox/checkbox';
import Search from '@/components/forms/search/search';
import {
	sortArrayofOptionsByProperty,
	compareArraysOfObjects
} from '@/utils/function';
import _ from 'lodash';

export default {
	name: 'SelectionList',
	model: {
		prop: 'itemSelected',
		event: 'change'
	},
	components: {
		Button,
		Checkbox,
		Search
	},
	props: {
		title: {
			type: String,
			default: ''
		},
		iconClassNameValue: {
			type: String,
			default: ''
		},
		itemAvailables: {
			type: Array,
			default: () => []
		},
		itemSelected: {
			type: Array,
			default: () => []
		},
		mapping: {
			type: Object,
			default: () => {
				return {
					id: 'id',
					name: 'label'
				};
			}
		},
		placeholder: {
			type: String,
			default: 'Search available clients'
		},
		availableText: {
			type: String
		},
		emptyText: {
			type: String,
			default:
				'The selected list is empty. Choose from the available list to make a selection.'
		},
		displaySelectAllButton: {
			type: Boolean,
			default: false
		},
		displayUnSelectAllButton: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			availables: [],
			selected: [],
			search: ''
		};
	},
	computed: {
		selectedItems() {
			return this.screenSize.mobile && this.selected.length > 0;
		},
		totalItemsAvailables() {
			return this.availables.length;
		},
		totalItemsSelected() {
			return this.selected.length;
		},
		disableSelectButton() {
			return !this.totalItemsAvailables;
		},
		disableUnselectButton() {
			return !this.totalItemsSelected;
		},
		sanitizeItemAvailables() {
			const selectedValues = new Set(
				this.itemSelected.map(item => item.value)
			);

			const sanitizedAvailable = this.itemAvailables.filter(
				item => !selectedValues.has(item.value)
			);

			return sanitizedAvailable;
		}
	},
	methods: {
		changeValueToSelected(item) {
			this.availables.splice(this.availables.indexOf(item), 1);
			this.selected.push(item);
			this.selected = sortArrayofOptionsByProperty(this.selected);
			this.emitChanges();
		},
		changeValueToAvailable(item) {
			this.selected.splice(this.selected.indexOf(item), 1);
			this.availables.push(item);
			this.availables = sortArrayofOptionsByProperty(this.availables);
			this.emitChanges();
		},
		unSelectAll() {
			this.availables = this.availables.concat(this.selected);
			this.selected = [];
			this.emitChanges();
		},
		SelectAll() {
			this.selected = this.selected.concat(this.availables);
			this.availables = [];
			this.emitChanges();
		},
		emitChanges() {
			this.$parent.$emit('change', this.selected);
			this.$emit('change', this.selected);
		},
		filterFormBySearch(val) {
			if (val === '') {
				this.availables = this.restoreCustomersBySelectedItems();
				return;
			}

			const sortedAvailables = sortArrayofOptionsByProperty(
				this.availables
			);
			this.availables = sortedAvailables.filter(
				item =>
					item.text &&
					item.value &&
					item.text.toLowerCase().includes(val.toLowerCase())
			);
			return;
		},
		restoreCustomersBySelectedItems() {
			const findDeselectedClients = (originalItemsSelected, selected) => {
				if (_.isEmpty(selected)) return originalItemsSelected;
				// Convert the current selected values to a set for quick lookup
				const selectedValues = new Set(
					selected.map(item => item.value)
				);
				// Filter the original items to find elements not in the current selected list
				const removedItems = originalItemsSelected.filter(
					item => !selectedValues.has(item.value)
				);
				return removedItems;
			};

			const deselectedClients = findDeselectedClients(
				this.itemSelected,
				this.selected
			);

			return [...this.sanitizeItemAvailables]
				.filter(x => !this.selected.find(y => x.text === y.text))
				.concat(deselectedClients);
		}
	},
	watch: {
		itemSelected: {
			deep: true,
			immediate: true,
			handler(val) {
				const self = this;
				this.selected = [...val];
				const hasSameObjValue = compareArraysOfObjects(
					[...this.availables],
					[...this.sanitizeItemAvailables]
				);
				if (
					(_.isEmpty(this.availables) && !this.search) ||
					!hasSameObjValue
				) {
					const data = [...this.sanitizeItemAvailables];
					this.availables = data.filter(function(item) {
						return (
							val
								.map(function(e) {
									return e[self.mapping.id];
								})
								.indexOf(item[self.mapping.id]) == -1
						);
					});
				}
			}
		},
		search: {
			deep: true,
			handler(val) {
				this.filterFormBySearch(decodeURIComponent(val));
			}
		}
	}
};
</script>

<style lang="scss">
/**
    * Selection List Group
    * Can use SCSS variables = TRUE
*/

.selection-list {
	display: grid;
	column-gap: $gutter-xl;
	grid-template-columns: 1fr;
	justify-content: space-between;

	@include mq($from: lg) {
		grid-template-columns: 1fr 1fr;
	}

	@include mq($until: sm-l) {
		margin-left: -8px;
		margin-right: -8px;
	}

	@include mq($from: sm-l) {
		border-radius: $form-border-radius;
	}

	&-container {
		border: 1px solid $neutral-3;
		border-radius: $border-radius;
		background: $white;
		padding: $padding-md;
		font-size: $default-font-size - 2px;
	}

	&__available,
	&__selected {
		display: flex;
		flex-direction: column;
		padding: $padding-md;

		> :not(:first-child) {
			flex-grow: 2;
		}
	}

	&__selected {
		.selection-list__items {
			max-height: 494px;
		}
	}

	&__list-help-txt {
		color: $neutral-2;

		@include mq($until: md-l) {
			text-align: center;
		}
	}

	&__list-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		font-size: $default-font-size - 2px;
		margin-bottom: $gutter-md;
	}

	&__list-title {
		font-size: inherit;
		font-weight: $medium-font-weight;
	}

	&__search {
		margin-bottom: $gutter-lg;
	}

	&__items {
		@include scrollbar('small');
		overflow-y: auto;
		max-height: 450px;
		padding: $padding-xs;
	}

	&__item {
		word-break: break-all;
		padding: $padding-sm 0;

		&:not(:last-child) {
			border-bottom: 1px solid $neutral-3;
		}

		@include mq($from: md) {
			&:first-child {
				padding-top: 0;
			}

			&:last-child {
				padding-bottom: 0;
			}
		}

		.checkbox__input {
			align-self: flex-start;
			margin-top: calc(
				(#{$label-line-height} - 16px) / 2
			); //16px is the height defined for the checkbox in checkbox.vue
			@include mq($until: lg) {
				margin-top: calc(
					(#{$label-mobile-line-height} - 16px) / 2
				); //16px is the height defined for the checkbox in checkbox.vue
			}
		}
	}
}
</style>
